import { useEffect, useState } from "react";
import { useRef } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

const EMPTY_FORM = {
  name: "", category: "", subcategory: "", price: "", sale_price: "",
  sizes: [], colors: [], images: [], tag: "", fabric: "", sleeve: "",
  pattern: "", collar: "", description: "", stock: "", is_featured: false,
  is_active: true,
};

const CATEGORIES = [
  "Indian Traditional Dresses",
  "Jewelry",
  "Summer Wear",
  "Winter Wear",
  "Cosmetics",
  "Miscellaneous",
];

const SUBCATEGORIES = {
  "Indian Traditional Dresses": [
    "All Indian Traditional Dresses",
    "Blouse", "Chudidar Set", "Kurtas", "Lehangas", "Sarees", "Sherwanis",
  ],
  "Jewelry": [
    "All Jewelry", "Anklets", "Bangles", "Chains", "Earrings", "Necklace Set", "Rings","Tikka","Waist Belt","Hathphool",
  ],
  "Summer Wear": [
    "All Summer Wear", "White Long Frocks", "Caps",
  ],
  "Winter Wear": [
  "All Winter Wear",
  "Abaya", "Incense Sticks", "Indie Pants", "Indie Skirts",
  "Scarfs and Pashmina", "Pashmina", "Cap", "Hand Gloves", "Socks",
],
  "Cosmetics": [
  "All Cosmetics",
  "Lipstick", "Foundation", "Skincare", "Perfumes", "Nail Care", "Eye Makeup",
],
};

const SIZE_OPTIONS  = ["XS", "S", "M", "L", "XL", "XXL"];
const TAG_OPTIONS   = ["Bestseller", "New", "Limited", "Premium", "Trending"];
const PRESET_COLORS = [
  { name: "Black",      hex: "#1a1a1a" },
  { name: "White",      hex: "#f5f5f4" },
  { name: "Red",        hex: "#e11d48" },
  { name: "Blue",       hex: "#2563eb" },
  { name: "Green",      hex: "#16a34a" },
  { name: "Yellow",     hex: "#eab308" },
  { name: "Pink",       hex: "#ec4899" },
  { name: "Purple",     hex: "#9333ea" },
  { name: "Orange",     hex: "#ea580c" },
  { name: "Gold",       hex: "#d97706" },
  { name: "Brown",      hex: "#92400e" },
  { name: "Beige",      hex: "#e8d5b7" },
  { name: "Cream",      hex: "#fdf6e3" },
  { name: "Gray",       hex: "#9ca3af" },
  { name: "Navy",       hex: "#1e3a5f" },
  { name: "Maroon",     hex: "#7f1d1d" },
  { name: "Olive",      hex: "#6b7c45" },
  { name: "Teal",       hex: "#0d9488" },
  { name: "Turquoise",  hex: "#06b6d4" },
  { name: "Lavender",   hex: "#c4b5fd" },
  { name: "Peach",      hex: "#fdba74" },
  { name: "Rose Gold",  hex: "#f4a4a4" },
  { name: "Mustard",    hex: "#ca8a04" },
  { name: "Coral",      hex: "#f97316" },
  { name: "Mint",       hex: "#6ee7b7" },
  { name: "Multicolor", hex: "#a855f7" },
];

// ── Helpers ───────────────────────────────────────────────────
function Badge({ label, color }) {
  const map = {
    green:  "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red:    "bg-red-100 text-red-700",
    gray:   "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${map[color]}`}>
      {label}
    </span>
  );
}

function stockBadge(stock) {
  if (stock === 0)  return <Badge label="Out of Stock" color="red"    />;
  if (stock <= 10)  return <Badge label="Low Stock"    color="yellow" />;
  return                   <Badge label="In Stock"     color="green"  />;
}

// ── Reusable image uploader (URL paste + file upload + thumbnails) ──
// Used both for the shared product image gallery and for each color's
// own gallery. `images` / `onChange` are scoped by the caller.
function ImageUploader({ images, onChange, compact = false }) {
  const [url,       setUrl]       = useState("");
  const [uploading, setUploading] = useState(false);

  function addUrl() {
    if (!url.trim()) return;
    onChange([...images, url.trim()]);
    setUrl("");
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    fetch(`${API}/api/admin/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
      body: fd,
    })
      .then(res => res.json())
      .then(data => { if (data.url) onChange([...images, data.url]); })
      .catch(() => alert("Upload failed."))
      .finally(() => { setUploading(false); e.target.value = ""; });
  }

  function remove(i) { onChange(images.filter((_, idx) => idx !== i)); }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input value={url} onChange={e => setUrl(e.target.value)}
          placeholder="Paste image URL"
          className={`flex-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-rose-400 ${compact ? "px-2.5 py-1.5" : "px-3 py-2"}`} />
        <button onClick={addUrl} type="button"
          className={`bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-800 ${compact ? "px-2.5 py-1.5" : "px-3 py-2"}`}>
          Add URL
        </button>
      </div>
      <label className={`inline-flex items-center gap-2 cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition ${compact ? "px-3 py-1.5" : "px-4 py-2"}`}>
        {uploading ? "Uploading..." : "📁 Upload Image"}
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
      </label>
      <div className="flex flex-wrap gap-3 mt-3">
        {images.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img} alt="" className={`object-cover rounded-lg border border-gray-200 ${compact ? "w-16 h-16" : "w-20 h-20"}`} />
            <button onClick={() => remove(i)} type="button"
              className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Kept as an alias so any other usage of the old name still works.
const ImageInput = ImageUploader;

// ── Color Input (with per-color image galleries) ────────────────
function ColorInput({ colors, onChange }) {
  const [search,        setSearch]        = useState("");
  const [open,          setOpen]          = useState(false);
  const [customHex,     setCustomHex]     = useState("#000000");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const filtered = PRESET_COLORS.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  function addColor(preset) {
    const hex  = preset.name === "Custom" ? customHex : preset.hex;
    const name = preset.name === "Custom" ? "Custom"  : preset.name;
    if (colors.some(c => c.name.toLowerCase() === name.toLowerCase())) return;
    onChange([...colors, { name: name.toLowerCase(), hex, images: [] }]);
    setSearch("");
    setOpen(false);
  }

  function remove(i) {
    onChange(colors.filter((_, idx) => idx !== i));
    if (expandedIndex === i) setExpandedIndex(null);
  }

  function setColorImages(i, newImages) {
    onChange(colors.map((c, idx) => idx === i ? { ...c, images: newImages } : c));
  }

  return (
    <div>
      <div className="relative mb-3" ref={ref}>
        <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:border-rose-400 bg-white">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
          </svg>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search color (e.g. Red, Navy, Gold...)"
            className="flex-1 text-sm outline-none bg-transparent"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setOpen(false); }}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
          )}
        </div>

        {open && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No colors found</p>
            ) : (
              filtered.map(c => (
                <button
                  key={c.name} type="button" onClick={() => addColor(c)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-rose-50 transition text-left"
                >
                  <div className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0" style={{ backgroundColor: c.hex }} />
                  <span className="text-sm text-gray-700">{c.name}</span>
                  {colors.some(x => x.name.toLowerCase() === c.name.toLowerCase()) && (
                    <span className="ml-auto text-[10px] text-rose-400 font-semibold">Added</span>
                  )}
                </button>
              ))
            )}
            <div className="flex items-center gap-3 px-4 py-2.5 border-t border-gray-100">
              <input type="color" value={customHex} onChange={e => setCustomHex(e.target.value)}
                className="w-5 h-5 rounded-full border border-gray-200 cursor-pointer flex-shrink-0" />
              <span className="text-sm text-gray-500 flex-1">Custom color</span>
              <button type="button" onClick={() => addColor({ name: "Custom", hex: customHex })}
                className="text-xs bg-rose-500 text-white px-3 py-1 rounded-full hover:bg-rose-600 transition">
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      {colors.length > 0 && (
        <p className="text-xs text-gray-400 mb-2">
          Tap a color's photo icon to add photos just for that color. Colors without their own photos will fall back to the shared images below.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {colors.map((c, i) => {
          const colorImages = c.images || [];
          const isExpanded  = expandedIndex === i;
          return (
            <div key={i} className={`rounded-xl border transition-colors ${isExpanded ? "border-rose-300 bg-rose-50/30" : "border-gray-100"}`}>
              <div className="flex items-center gap-1.5 px-1 py-1">
                <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1 flex-shrink-0">
                  <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" style={{ background: c.hex }} />
                  <span className="text-xs text-gray-700 capitalize">{c.name}</span>
                  <button onClick={() => remove(i)} type="button"
                    className="text-gray-400 hover:text-red-500 text-xs ml-1 leading-none">×</button>
                </div>

                <button
                  type="button"
                  onClick={() => setExpandedIndex(isExpanded ? null : i)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                    isExpanded
                      ? "bg-rose-500 text-white border-rose-500"
                      : colorImages.length > 0
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-300"
                      : "bg-white text-gray-500 border-gray-200 hover:border-rose-300"
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 7.5L12 3m0 0L7.5 7.5M12 3v13.5" />
                  </svg>
                  {colorImages.length > 0 ? `${colorImages.length} photo${colorImages.length > 1 ? "s" : ""}` : "Add photos"}
                </button>
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 pt-1">
                  <ImageUploader
                    compact
                    images={colorImages}
                    onChange={(imgs) => setColorImages(i, imgs)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Product Modal ─────────────────────────────────────────────
function ProductModal({ product, onClose, onSaved }) {
  const [form,    setForm]    = useState(product || EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const isEdit = !!product;

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function toggleSize(s) {
    const sizes = form.sizes.includes(s)
      ? form.sizes.filter(x => x !== s)
      : [...form.sizes, s];
    set("sizes", sizes);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const url    = isEdit ? `${API}/api/admin/products/${product.id}` : `${API}/api/admin/products`;
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          ...form,
          price:      parseFloat(form.price),
          sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
          stock:      parseInt(form.stock) || 0,
          is_active:  form.is_active ? 1 : 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Error saving product."); return; }
      onSaved(data, isEdit);
      onClose();
    } catch {
      setError("Could not connect to server.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Product" : "Add New Product"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
          </div>

          {/* Category + Subcategory */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select value={form.category} onChange={e => { set("category", e.target.value); set("subcategory", ""); }} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
                <option value="">Select...</option>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
  {form.category === "Miscellaneous" ? (
    <input
      type="text"
      value={form.subcategory || ""}
      onChange={e => set("subcategory", e.target.value)}
      placeholder="e.g. Gift Sets, Accessories, Home Decor..."
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
    />
  ) : (
    <select
      value={form.subcategory || ""}
      onChange={e => set("subcategory", e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
      disabled={!form.category}
    >
      <option value="">Select...</option>
      {(SUBCATEGORIES[form.category] || []).map(s => (
        <option key={s}>{s}</option>
      ))}
    </select>
  )}
</div>
          </div>

          {/* Price + Sale Price + Stock */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($) *</label>
              <input type="number" value={form.price} onChange={e => set("price", e.target.value)} required min="0" step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price ($)</label>
              <input type="number" value={form.sale_price || ""} onChange={e => set("sale_price", e.target.value)} min="0" step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
              <input type="number" value={form.stock} onChange={e => set("stock", e.target.value)} min="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
            </div>
          </div>

          {/* Sizes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sizes</label>
            <div className="flex gap-2 flex-wrap">
              {SIZE_OPTIONS.map(s => (
                <button key={s} type="button" onClick={() => toggleSize(s)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition
                    ${form.sizes.includes(s)
                      ? "bg-rose-500 text-white border-rose-500"
                      : "bg-white text-gray-600 border-gray-300 hover:border-rose-400"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Colors (each color can carry its own photo set) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Colors</label>
            <ColorInput colors={form.colors} onChange={v => set("colors", v)} />
          </div>

          {/* Images (shared fallback gallery) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
            <p className="text-xs text-gray-400 mb-2">Used when a selected color has no photos of its own.</p>
            <ImageUploader images={form.images} onChange={v => set("images", v)} />
          </div>

          {/* Tag */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
            <select value={form.tag || ""} onChange={e => set("tag", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
              <option value="">None</option>
              {TAG_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          {/* Fabric + Sleeve + Pattern + Collar */}
          <div className="grid grid-cols-2 gap-4">
            {[["fabric","Fabric"],["sleeve","Sleeve"],["pattern","Pattern"],["collar","Collar"]].map(([key, label]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input value={form[key] || ""} onChange={e => set(key, e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400" />
              </div>
            ))}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={3} value={form.description || ""} onChange={e => set("description", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400 resize-none" />
          </div>

          {/* Featured + Active Status */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
            <p className="text-sm font-semibold text-gray-700">Product Status</p>

            {/* Active / Inactive toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700 font-medium">Active (Live on store)</p>
                <p className="text-xs text-gray-400">Inactive products are hidden from customers</p>
              </div>
              <button
                type="button"
                onClick={() => set("is_active", !form.is_active)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                  form.is_active ? "bg-emerald-500" : "bg-gray-300"
                }`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  form.is_active ? "translate-x-7" : "translate-x-1"
                }`} />
              </button>
            </div>

            {/* Featured toggle */}
            <div className="flex items-center justify-between border-t border-gray-200 pt-3">
              <div>
                <p className="text-sm text-gray-700 font-medium">Featured Product</p>
                <p className="text-xs text-gray-400">Show on homepage New Arrivals section</p>
              </div>
              <button
                type="button"
                onClick={() => set("is_featured", !form.is_featured)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                  form.is_featured ? "bg-amber-500" : "bg-gray-300"
                }`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  form.is_featured ? "translate-x-7" : "translate-x-1"
                }`} />
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-bold transition">
              {loading ? "Saving..." : isEdit ? "Update Product" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Products Page ────────────────────────────────────────
export default function Products() {
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [modal,     setModal]     = useState(null);
  const [search,    setSearch]    = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [deleting,  setDeleting]  = useState(null);

  useEffect(() => { fetchProducts(); }, []);

  async function fetchProducts() {
    try {
      const res  = await fetch(`${API}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setProducts(data);
    } catch {
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }

  function onSaved(product, isEdit) {
    if (isEdit) {
      setProducts(ps => ps.map(p => p.id === product.id ? product : p));
    } else {
      setProducts(ps => [product, ...ps]);
    }
  }

  // ── Deactivate (soft delete) ──────────────────────────────
  async function handleDelete(id) {
    if (!window.confirm("Deactivate this product? It will be hidden from the store but can be restored.")) return;
    setDeleting(id);
    try {
      await fetch(`${API}/api/admin/products/${id}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });
      setProducts(ps => ps.map(p => p.id === id ? { ...p, is_active: 0 } : p));
    } catch {
      alert("Failed to deactivate product.");
    } finally {
      setDeleting(null);
    }
  }

  // ── Restore (reactivate) ──────────────────────────────────
  async function handleRestore(id) {
    if (!window.confirm("Restore this product? It will be visible in the store again.")) return;
    setDeleting(id);
    try {
      await fetch(`${API}/api/admin/products/${id}/restore`, {
        method:  "PATCH",
        headers: { Authorization: `Bearer ${token()}` },
      });
      setProducts(ps => ps.map(p => p.id === id ? { ...p, is_active: 1 } : p));
    } catch {
      alert("Failed to restore product.");
    } finally {
      setDeleting(null);
    }
  }

  // ── Permanent delete ──────────────────────────────────────
  async function handlePermanentDelete(id) {
    if (!window.confirm("⚠️ PERMANENT DELETE\n\nThis cannot be undone. Are you sure?")) return;
    setDeleting(id);
    try {
      await fetch(`${API}/api/admin/products/${id}?permanent=true`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });
      setProducts(ps => ps.filter(p => p.id !== id));
    } catch {
      alert("Failed to delete product.");
    } finally {
      setDeleting(null);
    }
  }

  // Filter
  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat    = catFilter ? p.category === catFilter : true;
    const matchStatus = statusFilter === "all" ? true
      : statusFilter === "active"   ? p.is_active === 1
      : statusFilter === "inactive" ? p.is_active === 0
      : true;
    return matchSearch && matchCat && matchStatus;
  });

  const activeCount   = products.filter(p => p.is_active === 1).length;
  const inactiveCount = products.filter(p => p.is_active === 0).length;

  if (loading) return (
    <div className="flex items-center justify-center h-full min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading products...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 text-sm mt-1">
            {products.length} total &nbsp;·&nbsp;
            <span className="text-emerald-600 font-medium">{activeCount} active</span>
            &nbsp;·&nbsp;
            <span className="text-gray-400">{inactiveCount} inactive</span>
          </p>
        </div>
        <button onClick={() => setModal("add")}
          className="bg-rose-600 hover:bg-rose-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition">
          + Add Product
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search products..."
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400"
        />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
          <option value="all">All Status</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Product</th>
                <th className="px-5 py-3 text-left">Category</th>
                <th className="px-5 py-3 text-left">Price</th>
                <th className="px-5 py-3 text-left">Stock</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Featured</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(p => {
                const imgs = typeof p.images === "string" ? JSON.parse(p.images) : p.images;
                const isActive = p.is_active === 1;
                return (
                  <tr key={p.id} className={`hover:bg-gray-50 transition ${!isActive ? "opacity-60" : ""}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={imgs?.[0]} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                        <div>
                          <p className="font-medium text-gray-900">{p.name}</p>
                          {p.tag && <span className="text-xs text-rose-500">{p.tag}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{p.category}</td>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-gray-900">${Number(p.price).toLocaleString()}</p>
                      {p.sale_price && <p className="text-xs text-rose-500">Sale: ${Number(p.sale_price).toLocaleString()}</p>}
                    </td>
                    <td className="px-5 py-3">{stockBadge(p.stock)}</td>
                    <td className="px-5 py-3">
                      {isActive
                        ? <Badge label="Active"   color="green" />
                        : <Badge label="Inactive" color="gray"  />}
                    </td>
                    <td className="px-5 py-3">
                      {p.is_featured ? <Badge label="⭐ Featured" color="yellow" /> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {/* Edit */}
                        <button
                          onClick={() => {
                            const parsed = {
                              ...p,
                              sizes:  typeof p.sizes  === "string" ? JSON.parse(p.sizes)  : p.sizes,
                              colors: (typeof p.colors === "string" ? JSON.parse(p.colors) : p.colors || [])
                                .map(c => ({ images: [], ...c })),
                              images: typeof p.images === "string" ? JSON.parse(p.images) : p.images,
                            };
                            setModal(parsed);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50 transition">
                          Edit
                        </button>

                        {/* Active → show Deactivate | Inactive → show Restore + Delete */}
                        {isActive ? (
                          <button
                            onClick={() => handleDelete(p.id)}
                            disabled={deleting === p.id}
                            className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition disabled:opacity-50">
                            {deleting === p.id ? "..." : "Deactivate"}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleRestore(p.id)}
                              disabled={deleting === p.id}
                              className="text-emerald-600 hover:text-emerald-800 text-xs font-medium px-2 py-1 rounded hover:bg-emerald-50 transition disabled:opacity-50">
                              {deleting === p.id ? "..." : "Restore"}
                            </button>
                            <button
                              onClick={() => handlePermanentDelete(p.id)}
                              disabled={deleting === p.id}
                              className="text-red-400 hover:text-red-600 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition disabled:opacity-50">
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-400">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <ProductModal
          product={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}